import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  message,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Divider,
  Space,
  Tag,
  Alert,
} from "antd";
import {
  UserOutlined,
  SaveOutlined,
  SafetyOutlined,
  BankOutlined,
  IdcardOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import MotionHoc from "../../../utils/MotionHoc";
import {
  baseUrl,
  GET_BY_ID,
  HEADERS_EXPORT,
  POST_USER,
} from "../../API/apiUrls";
import axios from "axios";
import RoleSelect from "../../../hook/RoleSelect";
import LoadCompanies from "../../../hook/LoadCompanies";
import TokenCheck from "../../../hook/TokenCheck";

const { Title, Text } = Typography;

// ========================================
// 🎨 Custom Components
// ========================================

/**
 * ProfileHeader - ส่วนหัวของโปรไฟล์พร้อม Avatar
 */
const ProfileHeader = ({ userData, activeStatus }) => (
  <Card
    style={{
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      marginBottom: 24,
      borderRadius: 12,
    }}
  >
    <div style={{ textAlign: "center" }}>
      <Avatar
        size={100}
        icon={<UserOutlined />}
        style={{
          background: "rgba(255, 255, 255, 0.2)",
          border: "4px solid white",
          marginBottom: 16,
        }}
      />
      <Title
        level={3}
        style={{
          color: "white",
          margin: 0,
          marginBottom: 8,
        }}
      >
        {`${userData?.FNAME || ""} ${userData?.LNAME || ""}`}
      </Title>
      <Space>
        <Tag
          icon={<UserOutlined />}
          color="blue"
          style={{ fontSize: 14, padding: "4px 12px" }}
        >
          @{userData?.USERNAME}
        </Tag>
        {activeStatus && (
          <Tag
            icon={<SafetyOutlined />}
            color="success"
            style={{ fontSize: 14, padding: "4px 12px" }}
          >
            ใช้งานอยู่
          </Tag>
        )}
      </Space>
    </div>
  </Card>
);

/**
 * InfoSection - ส่วนแสดงข้อมูล
 */
const InfoSection = ({ title, icon, children }) => (
  <div style={{ marginBottom: 24 }}>
    <Divider orientation="left">
      {icon} <span style={{ marginLeft: 8 }}>{title}</span>
    </Divider>
    {children}
  </div>
);

// ========================================
// 🎯 Main Component
// ========================================

const Main = () => {
  const [form] = Form.useForm();
  const [roleList] = RoleSelect();
  const { companiesList, loading: loadingCompanies } = LoadCompanies();
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const userCompany = localStorage.getItem("COMPANY_ID");
  const [signOut] = TokenCheck();
  // States
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleOption, setRoleOption] = useState([]);
  const [companiesOption, setCompaniesOption] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [dataToConfirm, setDataToConfirm] = useState({});
  const [activeStatus, setActiveStatus] = useState(true);

  // ========================================
  // 📊 Data Loading
  // ========================================

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (roleList.length > 0) {
      setOptionRole();
    }
  }, [roleList]);

  useEffect(() => {
    if (companiesList.length > 0) {
      setOptionCompanies();
    }
  }, [companiesList]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}${GET_BY_ID}${userId}`, {
        headers: HEADERS_EXPORT,
      });

      if (response.status === 200 && response.data) {
        setUserData(response.data);
        setDataDefault(response.data);
        setActiveStatus(response.data.ACTIVE_STATUS);
        console.log("User data loaded:", response.data);
      }
    } catch (error) {
      if (error.response?.data === "Session expired") {
        signOut();
      } else {
        // เคลียร์ค่า state กลับเป็นค่าว่างเพื่อไม่ให้มีข้อมูลเก่าค้าง

        console.error("Error fetching lawyers data:", error);

        // แจ้งเตือนข้อผิดพลาด
        const errorMessage =
          error.response?.data?.message ||
          "เกิดข้อผิดพลาดในการดึงข้อมูลทนายความ";
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // 🎬 Event Handlers
  // ========================================

  const setOptionRole = () => {
    const options = roleList.map((item) => ({
      value: item.id,
      label: item.description_th,
    }));
    setRoleOption(options);
  };

  const setOptionCompanies = () => {
    const options = companiesList
      .filter((item) => item.id === 1 || item.id === 2 || item.id === 3)
      .map((item) => ({
        value: item.id,
        label: item.company_name,
        address: item.address,
      }));
    setCompaniesOption(options);

    const companyDefault = options?.find(
      (item) => item.value === parseInt(userCompany),
    );

    if (companyDefault) {
      form.setFieldsValue({
        COMPANY_SHOW: companyDefault.label,
      });
      setCurrentCompany(companyDefault);
    }
  };

  const setDataDefault = (data) => {
    if (data) {
      console.log("data", data);

      form.setFieldsValue({
        USERNAME: data.USERNAME || "-",
        FNAME: data.FNAME || "-",
        LNAME: data.LNAME || "-",
        NNAME: data.NNAME || "-",
        LICENCE_NO: data.LICENCE_NO || "-",
        COMPANY_SHOW: data.COMPANY_ID,
        // ROLE_ID: data.ROLE_ID || "-",
        ACTIVE_STATUS: data.ACTIVE_STATUS,
      });
    }
  };

  const onSwitchChange = (checked) => {
    setActiveStatus(checked);
    console.log(`Active status changed to: ${checked}`);
  };

  const onCompanyChange = (value) => {
    const selected = companiesOption.find((item) => item.value === value);
    setCurrentCompany(selected);
    console.log("Company changed to:", selected);
  };
  console.log(dataToConfirm);

  const onFinish = (values) => {
    console.log("Form values:", values);
    console.log("userData", userData);
    setDataToConfirm({
      ...userData,
      ...dataToConfirm,
      USERNAME: values.USERNAME,
      TNAME: userData.TNAME,
      FNAME: values.FNAME,
      LNAME: values.LNAME,
      NNAME: values.NNAME,
      // LICENCE_NO: values.LICENCE_NO || userData.LICENCE_NO,
      COMPANY_ID: values.COMPANY_ID || currentCompany?.value,
      // ROLE_ID: values.ROLE_ID,
      ACTIVE_STATUS: values.ACTIVE_STATUS,
      // book_bank: values.bookBank || userData.book_bank,
    });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Form validation failed:", errorInfo);
    message.error("กรุณากรอกข้อมูลที่มีเครื่องหมาย * ให้ครบถ้วน");
  };

  // ========================================
  // 💾 Save Data
  // ========================================

  const sendData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}${POST_USER}`,
        dataToConfirm,
        { headers: HEADERS_EXPORT },
      );

      if (response.status === 200) {
        message.success(`อัพเดทข้อมูลสำเร็จ`);

        // Update localStorage
        localStorage.setItem("USERNAME", dataToConfirm.USERNAME);
        localStorage.setItem("TNAME", dataToConfirm.TNAME);
        localStorage.setItem("FNAME", dataToConfirm.FNAME);
        localStorage.setItem("LNAME", dataToConfirm.LNAME);
        localStorage.setItem("NNAME", dataToConfirm.NNAME);
        localStorage.setItem("LICENCE_NO", dataToConfirm.LICENCE_NO);
        localStorage.setItem("COMPANY_ID", dataToConfirm.COMPANY_ID);
        localStorage.setItem("ACTIVE_STATUS", dataToConfirm.ACTIVE_STATUS);
        console.log("dataToConfirm", dataToConfirm);

        // setTimeout(() => {
        //   window.location.reload();
        // }, 1000);
      } else if (response.status === 201) {
        message.warning("ลงทะเบียนใหม่");
      } else {
        message.error("ไม่สามารถบันทึกข้อมูลได้");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      message.error("เกิดข้อผิดพลาดในการอัพเดทข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    console.log("Data to confirm:", dataToConfirm);
    sendData();
  };

  // ========================================
  // 🎨 Render
  // ========================================

  // Check if user can change company
  const canChangeCompany = userData?.ROLE_ID === 1 || userData?.ROLE_ID === 2;

  if (!userData) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" tip="กำลังโหลดข้อมูล..." />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: 1000,
        margin: "0 auto",
        minHeight: "100vh",
        background: "#f0f2f5",
      }}
    >
      <Spin spinning={loading} size="large" tip="กำลังบันทึก...">
        {/* Profile Header */}
        <ProfileHeader userData={userData} activeStatus={activeStatus} />

        {/* Main Form */}
        <Card
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <Form
            form={form}
            name="user-profile"
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            size="large"
          >
            {/* สถานะการใช้งาน */}
            <InfoSection
              title="สถานะการใช้งาน"
              icon={<SafetyOutlined style={{ color: "#52c41a" }} />}
            >
              <Alert
                message={
                  activeStatus
                    ? "บัญชีนี้ใช้งานอยู่"
                    : "บัญชีนี้ถูกระงับการใช้งาน"
                }
                description={
                  activeStatus
                    ? "บัญชีของคุณสามารถเข้าใช้งานระบบได้ตามปกติ"
                    : "กรุณาติดต่อผู้ดูแลระบบเพื่อเปิดใช้งานบัญชี"
                }
                type={activeStatus ? "success" : "warning"}
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                label="เปิด/ปิดการใช้งาน"
                name="ACTIVE_STATUS"
                valuePropName="checked"
              >
                <Switch
                  onChange={onSwitchChange}
                  checkedChildren="เปิด"
                  unCheckedChildren="ปิด"
                />
              </Form.Item>
            </InfoSection>

            {/* ข้อมูลส่วนตัว */}
            <InfoSection
              title="ข้อมูลส่วนตัว"
              icon={<IdcardOutlined style={{ color: "#1890ff" }} />}
            >
              <Form.Item label="ชื่อผู้ใช้ (Username)" name="USERNAME">
                <Input
                  prefix={<UserOutlined style={{ color: "#999" }} />}
                  disabled
                  style={{ background: "#f5f5f5" }}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="ชื่อ"
                    name="FNAME"
                    rules={[{ required: true, message: "กรุณากรอกชื่อ" }]}
                  >
                    <Input
                      placeholder="กรอกชื่อ"
                      prefix={<EditOutlined style={{ color: "#999" }} />}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="นามสกุล"
                    name="LNAME"
                    rules={[{ required: true, message: "กรุณากรอกนามสกุล" }]}
                  >
                    <Input
                      placeholder="กรอกนามสกุล"
                      prefix={<EditOutlined style={{ color: "#999" }} />}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="ชื่อเล่น"
                name="NNAME"
                rules={[{ required: true, message: "กรุณากรอกชื่อเล่น" }]}
              >
                <Input
                  placeholder="กรอกชื่อเล่น"
                  prefix={<EditOutlined style={{ color: "#999" }} />}
                />
              </Form.Item>
              <Space>
                <Form.Item label="ไลน์ปัจจุบัน" name="line">
                  <Input
                    placeholder="กรุณา login line ก่อน"
                    prefix={<EditOutlined style={{ color: "#999" }} />}
                    disabled
                  />
                </Form.Item>

                <Popconfirm
                  title="ยืนยันการลบข้อมูล"
                  description="เมื่อลบข้อมูลออกไปแล้วจะไม่สามารถรับการแจ้งเตือนที่ระบบส่งให้ได้้ แน่ใจ?"
                  onConfirm={() => {
                    setDataToConfirm({
                      ...dataToConfirm,
                      line_uid: null,
                      LICENCE_NO: null,
                    });
                  }}
                  okText="ยืนยัน"
                  cancelText="ยกเลิก"
                  okButtonProps={{ size: "large" }}
                  cancelButtonProps={{ size: "large" }}
                >
                  <Button
                    danger
                    size="large"
                    icon={<DeleteOutlined />}
                    style={{
                      minWidth: 100,
                      height: 40,
                      fontSize: 16,
                      marginTop: 10,
                    }}
                  >
                    ลบการผูกไลน์
                  </Button>
                </Popconfirm>
              </Space>
            </InfoSection>

            {/* ข้อมูลองค์กร */}
            <InfoSection
              title="ข้อมูลองค์กร"
              icon={<BankOutlined style={{ color: "#722ed1" }} />}
            >
              <Alert
                message="บริษัทที่สังกัดปัจจุบัน"
                description={
                  <Space>
                    <BankOutlined />
                    <Text strong>
                      {companiesOption.find(
                        (item) => item.value === parseInt(userCompany),
                      )?.label || "-"}
                    </Text>
                  </Space>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Form.Item
                label="เปลี่ยนบริษัทที่สังกัด"
                name="COMPANY_ID"
                help={
                  !canChangeCompany && "คุณไม่มีสิทธิ์เปลี่ยนบริษัท (Admin)"
                }
              >
                <Select
                  showSearch
                  placeholder="เลือกบริษัท"
                  optionFilterProp="label"
                  options={companiesOption}
                  onChange={onCompanyChange}
                  disabled={!canChangeCompany}
                  suffixIcon={<BankOutlined />}
                />
              </Form.Item>

              {/* <Form.Item label="ตำแหน่ง" name="ROLE_ID">
                <Select
                  showSearch
                  placeholder="เลือกตำแหน่ง"
                  optionFilterProp="label"
                  options={roleOption}
                  disabled
                  suffixIcon={<TeamOutlined />}
                  style={{ background: "#f5f5f5" }}
                />
              </Form.Item> */}
            </InfoSection>

            {/* ปุ่มบันทึก */}
            <div style={{ textAlign: "center", marginTop: 32 }}>
              <Popconfirm
                title="ยืนยันการบันทึกข้อมูล"
                description="คุณต้องการบันทึกการเปลี่ยนแปลงข้อมูลโปรไฟล์หรือไม่?"
                onConfirm={handleConfirm}
                okText="ยืนยัน"
                cancelText="ยกเลิก"
                okButtonProps={{ size: "large" }}
                cancelButtonProps={{ size: "large" }}
              >
                <Button
                  color="blue"
                  variant="outlined"
                  htmlType="submit"
                  size="large"
                  icon={<SaveOutlined />}
                  style={{
                    minWidth: 200,
                    height: 48,
                    fontSize: 16,
                  }}
                >
                  บันทึกข้อมูล
                </Button>
              </Popconfirm>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

const Profile = MotionHoc(Main);
export default Profile;
